"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnTransformOffPageConnectorGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const TransformNetLabel_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformNetLabel");
const OrcadDsnSymbolUtils_1 = require("./OrcadDsnSymbolUtils");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const OrcadDsnBusUtils_1 = require("./OrcadDsnBusUtils");
const portOccurrenceNormalization_1 = require("../../../portOccurrenceNormalization");
const orcadIntersheetReferences_1 = require("../../../orcadIntersheetReferences");
class OrcadDsnTransformOffPageConnectorGenerator {
    constructor() {
        this.runsFor = [TransformSchematic_1.TransformSchematic];
    }
    *generate(parent, _rootContext) {
        const page = parent.hierarchyEntry.page;
        const design = parent.hierarchyEntry.design;
        for (const connector of page.OffPageConnector ?? []) {
            const resolution = this.findOffPageSymbol(design.Cache?.OffPageSymbol, connector);
            const symbolDef = resolution.symbol;
            if (resolution.ambiguous) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'symbol-variant-ambiguous',
                    message: `[symbol-variant-ambiguous] off-page symbol="${connector.SymbolName}" library="${connector.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                continue;
            }
            if (!symbolDef) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-off-page-connector-missing-symbol',
                    message: `Could not find off-page symbol definition for "${connector.SymbolName}"`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                continue;
            }
            const pins = symbolDef.PinScalar;
            if (pins.length === 0) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-off-page-connector-missing-pin',
                    message: `Off-page symbol definition for "${connector.SymbolName}" has no pin`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                continue;
            }
            if (pins.length > 1) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-multi-pin-connector-assumed',
                    message: `[multi-pin-connector-assumed] off-page connector "${connector.Name}" draws ${pins.length} connection points; every one carries the connector's own net`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            const busDetails = (0, OrcadDsnBusUtils_1.getBusDetails)(connector.Name);
            const netLabelPositions = pins.map((connectorPin) => (0, OrcadDsnSymbolUtils_1.computeSymbolPinPosition)([connectorPin.HotptX, connectorPin.HotptY], symbolDef.BoundingBox, connector.LocX, connector.LocY, connector.Rotation ?? 0, connector.Mirror ?? 0));
            const connectorMetadata = {
                'off-page-connector': { connectorDbId: connector.DbId.toString() },
            };
            let netLabel;
            for (const position of netLabelPositions) {
                const label = new TransformNetLabel_1.TransformNetLabel(parent, {
                    position,
                    originalLabel: connector.Name,
                    labelType: 'off-page-connector',
                    dimensions: busDetails ? ['bus'] : undefined,
                    strategyMetadata: connectorMetadata,
                });
                netLabel ??= label;
                yield label;
            }
            if (!netLabel) {
                continue;
            }
            if (busDetails) {
                const members = (0, portOccurrenceNormalization_1.expandBusPortName)(connector.Name);
                if (this.shouldExpandOffPageBus(page, members)) {
                    for (const member of members) {
                        for (const position of netLabelPositions) {
                            yield new TransformNetLabel_1.TransformNetLabel(parent, {
                                position,
                                originalLabel: member.name,
                                labelType: 'off-page-connector',
                                dimensions: [(0, OrcadDsnBusUtils_1.busDimensionName)(member)],
                                strategyMetadata: connectorMetadata,
                            });
                        }
                    }
                }
            }
            const { drawing, imageExtractions } = (0, OrcadDsnSymbolUtils_1.generateSymbolGroupDrawing)({
                parent,
                symbolDef,
                boundingBox: symbolDef.BoundingBox,
                locX: connector.LocX,
                locY: connector.LocY,
                rotation: connector.Rotation,
                mirror: connector.Mirror ?? 0,
                name: connector.Name,
                displayProps: connector.OffPageConnectorDisplayProp,
                userProps: connector.OffPageConnectorUserProp,
                netRefObject: netLabel,
                crossReferenceSource: { kind: orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector, dbId: connector.DbId },
            });
            yield drawing;
            yield* imageExtractions;
        }
    }
    shouldExpandOffPageBus(page, expandedMembers) {
        if (expandedMembers.length === 0) {
            return false;
        }
        const expandedNames = new Set(expandedMembers.map((m) => m.name));
        for (const netBus of page.NetBus ?? []) {
            for (const ns of netBus.NetScalar ?? []) {
                if (ns.Name && expandedNames.has(ns.Name)) {
                    return true;
                }
            }
        }
        for (const netScalar of page.NetScalar ?? []) {
            for (const wire of netScalar.WireScalars ?? []) {
                if ((wire.Alias ?? []).some((alias) => alias.Name !== undefined && expandedNames.has(alias.Name))) {
                    return true;
                }
            }
        }
        return false;
    }
    findOffPageSymbol(symbols, connector) {
        const symbolName = connector.SymbolName?.toString();
        if (!symbols || symbolName === undefined) {
            return { symbol: undefined, ambiguous: false };
        }
        return (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(symbols, symbolName, connector.LibName);
    }
}
exports.OrcadDsnTransformOffPageConnectorGenerator = OrcadDsnTransformOffPageConnectorGenerator;
//# sourceMappingURL=OrcadDsnTransformOffPageConnectorGenerator.js.map